/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Adapts the rule-map style objects used across the assignment, syllabus and
 * onboarding modules to emotion, which backs MUI v7's `styled`.
 *
 * A rule map is keyed by slot name (`{ root: {...}, title: {...} }`) and may
 * reference sibling slots with the `$slotName` syntax. Emotion has no notion of
 * slots, so each slot is given a stable class name and every `$slotName`
 * reference is rewritten to that class. The result is a single style object to
 * hand to `styled`, applied on the element that scopes the slots.
 *
 * @file createScopedStyles.js
 */

const GLOBAL_KEY = '@global';

/**
 * Builds the slot name to class name map for a rule map.
 *
 * @param {string} prefix unique prefix for the module, e.g. `aet-assignment-list-item`
 * @param {object} ruleMap slot keyed style object
 * @returns {object} slot name to class name
 */
export const createScopedClasses = (prefix, ruleMap) => Object.keys(ruleMap)
  .filter((slot) => slot !== GLOBAL_KEY)
  .reduce((acc, slot) => {
    acc[slot] = `${prefix}-${slot}`;

    return acc;
  }, {});

const rewriteSlotReferences = (selector, classes) => selector.replace(
  /\$([A-Za-z][\w]*)/g,
  (match, slot) => (classes[slot] ? `.${classes[slot]}` : match)
);

const rewriteRule = (rule, classes) => {
  if (rule === null || typeof rule !== 'object') {
    return rule;
  }

  if (Array.isArray(rule)) {
    return rule.map((entry) => rewriteRule(entry, classes));
  }

  return Object.keys(rule).reduce((acc, key) => {
    acc[rewriteSlotReferences(key, classes)] = rewriteRule(rule[key], classes);

    return acc;
  }, {});
};

/**
 * Converts a rule map into a style object for `styled`.
 *
 * Each slot matches both the scoping element itself and any descendant carrying
 * the slot class, so a slot can be used on the scope root or anywhere inside it.
 *
 * @param {object} ruleMap slot keyed style object
 * @param {object} classes slot name to class name, from `createScopedClasses`
 * @returns {object} emotion compatible style object
 */
export const createScopedStyles = (ruleMap, classes) => Object.keys(ruleMap)
  .filter((slot) => slot !== GLOBAL_KEY)
  .reduce((acc, slot) => {
    const className = classes[slot];
    acc[`&.${className}, & .${className}`] = rewriteRule(ruleMap[slot], classes);

    return acc;
  }, {});

/**
 * Extracts the `@global` rules of a rule map, for use with MUI's `GlobalStyles`.
 *
 * @param {object} ruleMap slot keyed style object
 * @param {object} classes slot name to class name, from `createScopedClasses`
 * @returns {object} emotion compatible style object
 */
export const createGlobalStyles = (ruleMap, classes) => rewriteRule(
  ruleMap[GLOBAL_KEY] || {},
  classes
);

/**
 * Builds unscoped rules for the named slots, for use with MUI's `GlobalStyles`.
 *
 * `createScopedStyles` confines a slot to the subtree of the element it is
 * applied to. That is the right default, but it cannot reach an element rendered
 * through a portal by a component that does not own the portal. Such slots need
 * their rules emitted as plain class selectors, which is how the JSS these rule
 * maps came from behaved for every slot.
 *
 * @param {object} ruleMap slot keyed style object
 * @param {object} classes slot name to class name, from `createScopedClasses`
 * @param {string[]} slots names of the slots to emit
 * @returns {object} emotion compatible style object
 */
export const createPortalSlotStyles = (ruleMap, classes, slots) => slots.reduce((acc, slot) => {
  if (ruleMap[slot]) {
    acc[`.${classes[slot]}`] = rewriteRule(ruleMap[slot], classes);
  }

  return acc;
}, {});
